import { inject, Injectable } from '@angular/core';

import { APP_CONFIG } from '../config/app-config.model';
import { ClockService } from './clock.service';

export type FreshnessTier = 'normal' | 'warning' | 'degraded';

/** Derived, never stored (data-model.md §6). */
export interface FreshnessReading {
  readonly ageSeconds: number;
  readonly tier: FreshnessTier;
  readonly displayText: string;
}

const SECONDS_PER_DAY = 24 * 60 * 60;

/**
 * Time-derived freshness reading (R-008): ageSeconds clamps at 0 for a viewer
 * clock behind the source (FR-020); tiers come from APP_CONFIG.freshness
 * (FR-016); ages beyond 24 h render an absolute localised timestamp.
 */
@Injectable({ providedIn: 'root' })
export class FreshnessService {
  private readonly clock = inject(ClockService);
  private readonly config = inject(APP_CONFIG);

  read(producedAt: number): FreshnessReading {
    const ageSeconds = Math.max(0, Math.floor((this.clock.now() - producedAt) / 1_000));
    const { warningSeconds, degradedSeconds } = this.config.freshness;
    const tier: FreshnessTier =
      ageSeconds >= degradedSeconds
        ? 'degraded'
        : ageSeconds >= warningSeconds
          ? 'warning'
          : 'normal';
    return { ageSeconds, tier, displayText: this.format(ageSeconds, producedAt) };
  }

  private format(ageSeconds: number, producedAt: number): string {
    if (ageSeconds >= SECONDS_PER_DAY) {
      return new Date(producedAt).toLocaleString();
    }
    const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: 'always' });
    if (ageSeconds < 60) return rtf.format(-ageSeconds, 'second');
    const ageMinutes = Math.floor(ageSeconds / 60);
    if (ageMinutes < 60) return rtf.format(-ageMinutes, 'minute');
    return rtf.format(-Math.floor(ageMinutes / 60), 'hour');
  }
}
